// Import Dexie and initialize the database
import Dexie from "dexie";

const db = new Dexie("sapu-app-db");
db.version(1).stores({
  chats: ",id"
});

export async function getAllChatsFromIndexedDB() {
  try {
    return await db.chats.toCollection().sortBy("time");
  } catch (error) {
    throw error;
  }
}

export async function clearAllChatsFromIndexedDB() {
  try {
    await db.chats.clear();
  } catch (error) {
    throw error;
  }
}

async function _generateSHA256(data) {
  const encoder = new TextEncoder();
  const dataStr = JSON.stringify(data);
  const dataBuffer = encoder.encode(dataStr);
  const hashBuffer = await crypto.subtle.digest("SHA-256", dataBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, "0")).join("");
}

export async function appendChatToIndexedDB(chat) {
  try {
    const hashHex = await _generateSHA256(chat);
    await db.chats.put(chat, hashHex);
  } catch (error) {
    throw error;
  }
}

// Helper to generate content signature for deduplication
function _getChatSignature(chat) {
  return `${chat.time}-${chat.from}-${chat.text || ""}-${chat.image || ""}`;
}
/**
 * Updates the local IndexedDB with chat history from the server.
 * Performs content-based deduplication to ensure only new messages are stored.
 * 
 * @param {Array} history - The list of chat messages to sync.
 * @returns {Promise<boolean>} - Returns true if any new messages were inserted, false otherwise.
 */
export async function updateChatHistoryInIndexedDB(history) {
  try {
    if (!history || history.length === 0) return false;

    // Get existing chats to check for duplicates by content
    const existingChats = await getAllChatsFromIndexedDB();
    const existingSignatures = new Set(existingChats.map(_getChatSignature));

    const newChats = [];
    const newKeys = [];

    for (const chat of history) {
      if (!existingSignatures.has(_getChatSignature(chat))) {
        // Only add if signature doesn't exist
        const hash = await _generateSHA256(chat);
        newChats.push(chat);
        newKeys.push(hash);
      }
    }

    if (newChats.length > 0) {
      await db.chats.bulkPut(newChats, newKeys);
      return true;
    }
    return false;
  } catch (error) {
    console.error("Failed to update chat history in IndexedDB:", error);
    throw error;
  }
}
